import classNames from 'classnames';
import _ from 'lodash';
import type { Op } from 'quill/core';
import { Delta } from 'quill/core';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import DeltaPreview from '../../components/DeltaPreview';
import { isEmojiChar } from '../../utils/emoji';
import styles from './CommentPreview.module.scss';
import { MentionBaseProvider } from '../../editor/mentions/MentionBase';

const mergeNormalLines = (content: Op[]) => {
  let hasMerged = false;
  const lines: { delta: Delta; attributes: Record<string, unknown> }[] = [];
  new Delta(content).eachLine((delta, attributes) => {
    const lastLine = _.last(lines);
    if (
      !lastLine ||
      !_.isEmpty(attributes) ||
      !_.isEmpty(lastLine.attributes)
    ) {
      lines.push({ delta, attributes });
    } else {
      lastLine.delta = lastLine.delta.insert(' ').concat(delta);
      hasMerged = true;
    }
  });
  if (!hasMerged) {
    return content;
  }
  let collapsedDelta = new Delta();
  lines.forEach((line) => {
    collapsedDelta = collapsedDelta.concat(line.delta);
    collapsedDelta.insert('\n', line.attributes);
  });
  return collapsedDelta.ops;
};

const getCharIfContentIsEmoji = (ops: Op[]) => {
  if (ops.length === 1 && typeof ops[0]?.insert === 'string') {
    const content = ops[0].insert.trim();
    if (isEmojiChar(content)) {
      return content;
    }
  }

  return null;
};

type CommentPreviewProps = {
  collapseLines?: boolean;
  content: Delta | Op[];
  link?: boolean;
  showHoverCard?: boolean;
  animated?: boolean;
  onResize?: () => void;
  initialHeight?: number;
} & (
  | {
      maxLines: number;
      disableLineClamp?: boolean;
      onCanBeClampedChange?: (canBeClamped: boolean) => void;
    }
  | {
      maxLines?: never;
      disableLineClamp?: never;
      onCanBeClampedChange?: never;
    }
);

export const ANIMATED_COMMENT_PREVIEW_DURATION = 0.2;

const AnimatedCommentPreview = ({
  disableLineClamp,
  maxLines,
  children,
  className,
  onResize,
  initialHeight,
}: {
  children: React.ReactNode;
  className?: string;
  initialHeight?: number;
} & Pick<
  CommentPreviewProps,
  'disableLineClamp' | 'maxLines' | 'onResize'
>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const commentPreviewId = disableLineClamp
    ? 'disabled'
    : `max-${maxLines ?? 'auto'}`;
  const [height, setHeight] = useState<number | undefined>(initialHeight);
  const previousHeightRef = useRef<number | undefined>(undefined);
  const previousWidthRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (height !== previousHeightRef.current) {
      previousHeightRef.current = height;
    }
  }, [height]);

  const shouldAnimate = height != null && previousHeightRef.current != null;

  useEffect(() => {
    function calculateHeight() {
      if (!containerRef.current) return;
      const elements = Array.from(containerRef.current.childNodes).filter(
        (node): node is HTMLElement => node instanceof HTMLElement,
      );
      const currentElement = elements.find(
        (el) => el.dataset.commentPreviewId === commentPreviewId,
      );
      const currentHeight = currentElement?.getBoundingClientRect().height;

      if (currentHeight != null) {
        setHeight(currentHeight);
      }
    }

    // We need to wait one tick to make sure that the truncation
    // of the comment preview in the children has been calculated and applied
    const timeout = setTimeout(calculateHeight);

    const observer = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect.width;
      if (previousWidthRef.current === width) return;
      previousWidthRef.current = width;
      calculateHeight();
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timeout);
      observer.disconnect();
    };
  }, [commentPreviewId]);

  return (
    <motion.div
      key="container"
      ref={containerRef}
      className={className}
      initial={false}
      animate={{ height }}
      style={{
        position: 'relative',
        overflow: 'hidden',
      }}
      transition={{
        duration: shouldAnimate ? ANIMATED_COMMENT_PREVIEW_DURATION : 0,
      }}
      onAnimationComplete={onResize}
    >
      <AnimatePresence>
        <motion.div
          initial={shouldAnimate ? { opacity: 0.5 } : false}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0.5 }}
          style={{ position: 'absolute' }}
          data-comment-preview-id={commentPreviewId}
          key={commentPreviewId}
          transition={{ duration: ANIMATED_COMMENT_PREVIEW_DURATION }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
};

const CommentPreview = ({
  collapseLines,
  content,
  animated,
  ...props
}: CommentPreviewProps) => {
  const { ops } = new Delta(content);
  const collapsedContent = useMemo(
    () => (collapseLines ? mergeNormalLines(ops) : ops),
    [ops, collapseLines],
  );
  const emojiChar = getCharIfContentIsEmoji(ops);
  const className = classNames('comment-preview', {
    [styles.singleEmoji]: emojiChar,
  });

  const inner = emojiChar || (
    <MentionBaseProvider containerType="comment">
      <DeltaPreview
        typography="comment"
        content={collapsedContent}
        {...props}
      />
    </MentionBaseProvider>
  );

  if (animated) {
    return (
      <AnimatedCommentPreview className={className} {...props}>
        {inner}
      </AnimatedCommentPreview>
    );
  }

  return <span className={className}>{inner}</span>;
};

export default CommentPreview;
